/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {computed, ComputedRef, defineComponent, onMounted, PropType, reactive, ref} from '@vue/composition-api';
import { SettingsContext } from "@/utilities/settings-context";
import { i18n, loadUserLanguageOrDefault } from '@/utilities/vue-i18n';
import { createPageContext } from "@/components/navbar-box/page-context";
import TransitionSlide from '@/components/transition-slide/transition-slide.vue';
import { getCountryList } from "@/functions/location-function";
import SelectII from "@/components/select-ii/select-ii.vue";
import OtpModal from '@/components/otp/otp-modal.vue';
import { OtpType } from '@/components/otp/otp-types';
import { alertDialogue, generalError } from '@/functions/system-modal-functions';
import {
    getExternalAuthenticationContext,
    onExternalRegistration,
    setAuthenticationToken
} from "@/functions/authentication-functions";
import {AxiosError} from "axios";
import {
    Accounts,
    GoogleCredential,
    RegistrationPayload,
    UserClaims, 
    validateEmail,
    validateNamePart, validatePassword, validatePhone
} from "@noordigitalagency/accounts-ledigajobb";
import VueCtkDateTimePicker from "vue-ctk-date-time-picker";
import { validateDateOfBirth } from '@/functions/validation-functions';

interface Shadow {
    [key: string]: { isValid: ComputedRef<boolean> };
}

export default defineComponent({
    components: {
        TransitionSlide,
        SelectII,
        OtpModal,
        VueCtkDateTimePicker
    },
    props: {
        registration: {
            type: Object as PropType<RegistrationPayload>,
            default: null,
            required: false
        }
    },
    setup(props, context) {

        const accounts = new Accounts(SettingsContext.constants.accountsApiDomainName);

        const googleButton = ref<HTMLElement>();

        const email = (typeof (context.root.$route.query.email) === 'string') && validateEmail(context.root.$route.query.email) ? context.root.$route.query.email : '';

        const settings = reactive({
            eula: false,
            passwordVisible: false,
            countries: [] as Array<{ id: string; label: string }>,
            showOtp: false,
            userRegistered: false,
            lockedEmail: email !== '',
            externalRegister: false,
            credential: null as unknown as GoogleCredential,
            date: {
                format: 'YYYY-MM-DD',
                locale: 'sv'
            }
        });

        const model = reactive({
            firstName: '',
            lastName: '',
            country: '',
            city: '',
            dateOfBirth: '',
            email: email,
            phoneNumber: '',
            password: ''
        });

        const shadow = reactive<Shadow>({
            firstName: {
                isValid: computed(() => validateNamePart(model.firstName))
            },
            lastName: {
                isValid: computed(() => validateNamePart(model.lastName))
            },
            country: {
                isValid: computed(() => validateNamePart(model.country))
            },
            city: {
                isValid: computed(() => validateNamePart(model.city))
            },
            email: {
                isValid: computed(() => validateEmail(model.email))
            },
            dateOfBirth: {
                isValid: computed(() => validateDateOfBirth(model.dateOfBirth))
            },
            phoneNumber: {
                isValid: computed(() => validatePhone(model.phoneNumber))
            },
            password: {
                isValid: computed(() => validatePassword(model.password, SettingsContext.get('signUp.passwordLength') as number))
            },
            eula: {
                isValid: computed(() => settings.eula)
            }
        });

        const states = reactive({

            inputs: computed(() => Object.keys(shadow).filter(value => Object.getOwnPropertyNames((shadow)[value]).includes('isValid') && value !== 'eula').every(key => (shadow[key] as { isValid: boolean }).isValid)),

            valid: computed(() => Object.keys(shadow).filter(value => Object.getOwnPropertyNames(shadow[value]).includes('isValid')).every(key => (shadow[key] as { isValid: boolean }).isValid)),

            error: ''
        });

        function fillUser(user: UserClaims) {

            model.firstName = user.firstName ?? '';

            model.lastName = user.lastName ?? '';

            model.email = user.email ?? '';

            settings.externalRegister = true;

            settings.lockedEmail = true;
        }

        onExternalRegistration(registrationPayload => {

            settings.credential = registrationPayload.googleCredential!;

            fillUser(registrationPayload.userClaims);
        });

        onMounted(async () => {

            settings.countries = await getCountryList();

            await loadUserLanguageOrDefault();

            settings.date.locale = i18n.locale;

            if (props.registration) {

                settings.credential = props.registration.googleCredential!;

                fillUser(props.registration.userClaims);

            } else {

                getExternalAuthenticationContext().insertGoogleButton(googleButton.value as HTMLElement, {width: `${googleButton.value?.clientWidth}px`, type: 'standard', text: "signup_with"});

                getExternalAuthenticationContext().showGoogleOneTap(SettingsContext.constants.stage !== 'production');
            }
        });

        function otpClosed() {
            
            settings.showOtp = false;

            window.location.replace("sign-in");
        }

        function otpShow() {

            getExternalAuthenticationContext().cancelGoogleOneTap();

            settings.showOtp = true;
        }

        async function repopulateFileSystemsAndRedirectToNewPage(token: string) {

            getExternalAuthenticationContext().cancelGoogleOneTap();

            accounts.updateToken(token);

            setAuthenticationToken(await accounts.makeRecruiter());

            await context.root.$router.replace({ name: 'Home' });
        }

        const otpConfirmed = async (payload: {otpCode: string}) => {

            try {
                states.error = '';

                await repopulateFileSystemsAndRedirectToNewPage(await accounts.confirmEmail(model.email, payload.otpCode));

                settings.showOtp = false;

            } catch {

                await generalError();
            }
        }

        const submit = async () => {

            if (settings.externalRegister) {

                try {

                    await repopulateFileSystemsAndRedirectToNewPage(await accounts.googleRegister({googleCredentials: settings.credential, user: model}));
                }
                catch (e) {

                    if ((e as AxiosError).response?.status == 412) {

                        /// If status == 412 the User need to activate.
                        otpShow();
                    }
                    else {

                        await generalError();
                    }
                }


            } else {

                try {

                    await accounts.register(model);

                    settings.userRegistered = true;

                    otpShow();

                } catch {

                    await generalError();
                }
            }


        };

        const pageContext = createPageContext({
            actions: [],
            title: { text: i18n.t('pages.register.new-account') as string },
            breadcrumb: {
                showHome: false,
                segments: []
            }
        });

        const updateDateOfBirth = (event: any) => {

            const newValue = event.target.value.replace(/[^\d]/g, ''); // Remove non-numeric characters except hyphens
            const datePattern = /^(\d{4})(\d{2})?(\d{2})?$/;

            if (!datePattern.test(newValue)) return;

            const dateOfBirth = newValue.replace(/^(\d{4})(\d{2})?(\d{2})?$/, (match: any, year: string, month: string, day: string) => {
                let result = year;
                if (month) result += '-' + month;
                if (day) result += '-' + day;
                return result;
            });
            
            model.dateOfBirth = dateOfBirth; 
        };

        return {
            passwordLength: SettingsContext.get('signUp.passwordLength') as number,
            settings,
            model,
            submit,
            shadow,
            pageContext,
            states,
            OtpType,
            otpClosed,
            otpConfirmed,
            googleButton,
            updateDateOfBirth,
        }
    }
});

